/**
 * Screen 2 of 4 — INTAKE.
 *
 * Collects the run inputs and posts them to
 * `POST /v1/valuation/engagements/{id}/recompute` (VAL-INDUSTRY-WIRING-1 R6C
 * — the same signed engine Tuning and Sign-off run on). Every number typed
 * here is an INPUT; nothing on this screen computes a value. The engine
 * composes the band on the signed methodology and the browser renders what
 * came back.
 *
 * Unit convention, inherited from `core/valuation` and not re-interpreted
 * here: rates are PERCENT-SCALE (4.35 means 4.35%), weights are fractions.
 *
 * Optional legs are genuinely optional — leaving the asset floor or the driver
 * scores blank drops that leg rather than substituting a guess. Comps are
 * optional in a subtler way: triangulation always needs a market leg, but with
 * the box empty the signed industry profile supplies one from its own band, so
 * blank is the normal path and typing comps is the override.
 *
 * VAL-PATH-FOOTER (P2a) — the one StepFooter moves this screen. Its save sends
 * whatever autosave has not stored yet; "Save and continue" then runs the
 * valuation (`runValuation`) and only moves on if the run succeeds. Autosave
 * stays as the safety net; its "Saved" line moved to the footer chip.
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from 'sonner'
import { StepFooter } from '@/components/valuation/StepFooter'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  fetchValuationMethodology,
  getValuationEngagement,
} from '@/api/valuation'
import { recomputeValuation } from '@/api/valuationB3'
import { isDisclaimerRequiredError } from '@/api/valuationDisclaimer'
import { formatApiError } from '@/utils/httpErrors'
import type {
  ValuationEngagement,
  ValuationMethodology,
} from '@/types/valuation'
import { DriverQuestionnaireSection } from './_DriverQuestionnaire'
import {
  ValuationStepper,
  ValueKindBadge,
  runCache,
} from './_shared'
import {
  buildIntakeBody,
  getMissingRequiredFields,
  intakeAutosavePatch,
} from './_valuationIntakeHelpers'
import { useValuationDraft } from './_valuationDraft'
import { useIntakeInputs } from './_useIntakeInputs'
import { useResumeScroll } from './_useResumeScroll'
import { useAutosave } from './_useAutosave'
import { IntakeMissingFields } from './_IntakeMissingFields'
import { StepBlocked, type StepSaveResult } from './_stepContext'
import { unansweredIdsFromError } from './_driverAnswers'

// Owner-approved (OWNER_APPROVALS.log 10-21 20:53).
const INTAKE_NEEDS_DRAFT = 'Not saved — finish Adjustments and rates first'
// WF-A-4: owner-approved, verbatim (OWNER_APPROVALS.log 2026-10-25 11:38 CT).
const WF_A_4 = "We couldn't run the valuation yet. Check your answers on Questions and click Save and continue again."

export default function ValuationIntake() {
  const { valuationId } = useParams<{ valuationId: string }>()
  const { user } = useAuth()

  const [engagement, setEngagement] = useState<ValuationEngagement | null>(null)
  const [methodology, setMethodology] = useState<ValuationMethodology | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  // WALKFIX-A AMEND-2 — the questions the last refused run named.
  const [refusal, setRefusal] = useState<{ questionIds: string[] } | null>(null)

  // VAL-INTAKE-1 D2/D3/D4, corrected by VAL-NAV-STATE-1 R3 — the one working
  // draft, shared with Tuning.
  const {
    draft,
    draftExtras,
    resuming,
    resumedAtLabel,
    loading: draftLoading,
    save: saveDraft,
    dismiss: dismissDraft,
  } = useValuationDraft(valuationId)
  const {
    // VAL-PATH-ADJUST P4b — the legs and rates are typed on step 3; Intake
    // still reads the draft's values to build the Run, never shows or saves them.
    riskFree, equityRiskPremium, sizePremium, companySpecificRisk, longTermGrowth,
    normalizedEarnings, baseCashFlow, replacementCompensation, growthRate, projectionYears,
    capWeight, compMultiples, totalAssets, totalLiabilities, fairValueAdjustments,
    driverAnswers, setDriverAnswers,
    hydrated,
    typedBeforeHydrate,
    startClean,
  } = useIntakeInputs(draft, draftLoading, dismissDraft, methodology?.input_defaults ?? null)
  const resumeQuestionId = useResumeScroll(methodology?.driver_questionnaire ?? null, driverAnswers, !!methodology && hydrated)
  // VAL-PATH-COMPANY (P3): I1-I9 moved to step 1, so an old #particulars link
  // resumes at the first unanswered question like any other visit.
  const focusQuestionId = resumeQuestionId
  // VAL-PATH-P7c: I10-I11 moved to the signer's Sign step (_SignDeclarations).

  // VAL-SAVE-ON-TYPE-1A — every change is saved ~1.5 s after typing stops
  // (DATA_PROVENANCE_RULE rule 1). Off until the draft has hydrated, so the
  // blank form can never overwrite it; no engine call from here.
  // VAL-PATH-ADJUST P4b — only the answers (P7b: the multiple override is on
  // Methodology and rates); step 3 starts the draft, so with none yet nothing
  // is saved and the line says why.
  const { patch: autosavePatch, problems: autosaveProblems } = intakeAutosavePatch({
    driverAnswers, hasDraft: !!draft,
  })
  const draftSave = useAutosave(
    autosavePatch,
    saveDraft,
    {
      enabled: hydrated && !!engagement,
      dirty: typedBeforeHydrate,
      blocked: draft ? null : INTAKE_NEEDS_DRAFT,
    },
  )
  const saveLine = autosaveProblems.length > 0 ? `Not saved — check ${autosaveProblems.join(', ')}`
    : draftSave.status === 'blocked' ? draftSave.message
    : draftSave.status === 'error' ? `Not saved — ${draftSave.message}`
    : draftSave.status === 'saving' ? 'Saving…'
    : null

  // The footer's save-first: send only what autosave has not stored (m3).
  async function intakeSave(): Promise<StepSaveResult> {
    if (autosaveProblems.length > 0) throw new Error(`Not saved — check ${autosaveProblems.join(', ')}`)
    const a = await draftSave.saveNow()
    return { changed: a.changed, savedAt: a.savedAt ?? null }
  }

  useEffect(() => {
    if (!valuationId) return
    let live = true
    Promise.all([getValuationEngagement(valuationId), fetchValuationMethodology()])
      .then(([en, meth]) => {
        if (!live) return
        setEngagement(en)
        setMethodology(meth)
      })
      .catch((err: unknown) => { if (live) setLoadError(formatApiError(err)) })
    return () => { live = false }
  }, [valuationId])

  // Runs after the footer's save, on "Save and continue"; a throw keeps the
  // person here with the message inline.
  async function runValuation(): Promise<void> {
    if (!valuationId) return
    // Blocked: the "Before you can run this valuation" list above names what is missing.
    if (missingFields.length > 0) throw new StepBlocked()
    const body = buildIntakeBody({
      riskFree, equityRiskPremium, sizePremium, companySpecificRisk, longTermGrowth,
      normalizedEarnings, replacementCompensation, baseCashFlow, growthRate, projectionYears, capWeight,
      compMultiples, totalAssets, totalLiabilities, fairValueAdjustments,
      driverAnswers, questions: methodology?.driver_questionnaire.questions ?? [],
    })
    if (typeof body === 'string') throw new Error(body)

    // VAL-PATH P5 — the footer has already saved this page (U2), so the run
    // reads the SAVED draft server-side (no body): what is valued is what is
    // stored, never page state. Same signed engine as Tuning/Sign-off.
    // WALKFIX-A F10 — a refused run (4xx) never shows the raw "400 - Bad
    // Request | CODE" line: the step's own lists above say what is missing.
    // VAL-PAY-FLOW-1: an owner who has not accepted the estimate disclaimer gets no value
    // back; move on to Your value, where the disclaimer (then payment) is taken.
    let disclaimerNeeded = false
    const result = await recomputeValuation(valuationId).catch((e: unknown) => {
      if (isDisclaimerRequiredError(e)) { disclaimerNeeded = true; return null }
      const status = (e as { response?: { status?: number } })?.response?.status
      // VAL-STRIPE-GATE-1: an owner who has not paid gets 402 here. The answers are
      // saved; Your value (next) shows the pay screen, so move on without a value.
      if (status === 402) return null
      if (status != null && status >= 400 && status < 500) {
        // AMEND-2 — the questions the server names go red; with none named,
        // WF-A-4 says where to look.
        const ids = unansweredIdsFromError(e)
        if (ids.length > 0) {
          setRefusal({ questionIds: ids })
          throw new StepBlocked()
        }
        throw new Error(WF_A_4)
      }
      throw e
    })
    // /recompute does not persist anything more than /run did — the band,
    // the provenance, the multiple, the overrides all live only in this
    // response and nowhere else, so cache it for the output screen. See
    // _shared.tsx.
    if (disclaimerNeeded || result === null) return
    runCache.save(valuationId, result)
    // D6 — the draft's `income_value` is not a form field on this screen,
    // it's this screen's OWN computed DCF result: the only defensible
    // income-approach dollar figure to hand Tuning is the one the signed
    // methodology just composed, not a number typed a second time. Saved
    // unconditionally; a save that cannot persist is a visible error.
    const postRunSaveError = await saveDraft({ income_value: result.income_value })
    if (postRunSaveError) toast.error(postRunSaveError)
    toast.success('Valuation run complete.')
    // The footer moves on to the next step on the rail once this resolves.
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <div className="rounded-lg border border-pal-error-border bg-pal-error-bg p-4 text-sm text-pal-error-text">{loadError}</div>
      </div>
    )
  }
  if (!engagement || !methodology) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <div>
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-2 h-4 w-40" />
        </div>
        <Skeleton className="h-10 w-full" />
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  const crossover = methodology.earnings_basis_rule.crossover_earnings_threshold
  // VAL-PATH-ADJUST P4b — every one of these is typed on step 3, so each links there.
  const missingFields = getMissingRequiredFields({ riskFree, companySpecificRisk, longTermGrowth, normalizedEarnings, growthRate, replacementCompensation, crossover, projectionYears, capWeight })
    .map(f => ({ ...f, href: `/valuation/${valuationId}/adjust` }))

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-pal-ink">{engagement.subject_company_name}</h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-pal-ink-muted">
          <ValueKindBadge kind={engagement.value_kind} owner={user?.role === 'valuation_owner'} />
          <span className="text-xs">{engagement.industry_label ?? engagement.industry ?? 'no industry set'}</span>
          {engagement.period_label && <span>· {engagement.period_label}</span>}
        </div>
        {resuming && (
          <p className="mt-2 flex flex-wrap items-center gap-2 rounded border border-pal-info-border bg-pal-info-bg px-3 py-2 text-sm text-blue-900">
            <span>Resuming your draft from {resumedAtLabel}.</span>
            <button
              type="button"
              onClick={startClean}
              className="font-medium underline underline-offset-2 hover:text-pal-link"
            >
              Start clean
            </button>
          </p>
        )}
        {saveLine && (
          <p className="mt-2 text-xs text-pal-ink-muted" role="status">{saveLine}</p>
        )}
      </div>

      <ValuationStepper current="intake" engagementId={valuationId} />

      <form onSubmit={e => e.preventDefault()} className="space-y-6">
        <DriverQuestionnaireSection
          questionnaire={methodology.driver_questionnaire}
          answers={driverAnswers}
          onAnswer={(questionId, value) =>
            setDriverAnswers(prev => ({ ...prev, [questionId]: value }))
          }
          persist={{ valuationId, saved: draftExtras.deferred_questions ?? undefined, enabled: !!draft }}
          focusQuestionId={focusQuestionId}
          refusal={refusal}
        />

        <IntakeMissingFields fields={missingFields} />
      </form>

      <StepFooter step="intake" engagementId={valuationId ?? ''} save={intakeSave} beforeNext={runValuation} />
    </div>
  )
}
